import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  IconArrow,
  MenuItem,
  MenuRenderer,
  Strong,
  Text,
  TooltipRenderer,
  useResponsiveValue,
} from 'braid-design-system';
import type { FC } from 'react';

import { BUCKETS } from 'src/stores/JobApplicationStore/types';
import mapBucketTypeToBuckets from 'src/stores/JobApplicationStore/utils/mapBucketToBuckets';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import translations from './.vocab';

interface MoveStatusMenuProps {
  currentBucket: BUCKETS;
  fullName: string;
  changeStatus: (bucket: BUCKETS) => Promise<void>;
}

export const MoveStatusMenu: FC<MoveStatusMenuProps> = ({
  currentBucket,
  fullName,
  changeStatus,
}) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();
  const responsiveValue = useResponsiveValue();
  const isMobile = responsiveValue({ mobile: true, tablet: false }) ?? false;

  const menuLabel =
    currentBucket === BUCKETS.INBOX
      ? t('Move fullName to another status', {
          fullName,
        })
      : t('Move fullName from bucket', {
          fullName,
          bucket: getLabelFromBucket(mapBucketTypeToBuckets(currentBucket)),
        });

  const menuItems = (
    <>
      <MenuItem
        data={{ cy: 'status-menu-item-inbox' }}
        key="inbox"
        onClick={() => {
          if (currentBucket !== BUCKETS.INBOX) {
            changeStatus(BUCKETS.INBOX);
          }
        }}
      >
        {currentBucket === BUCKETS.INBOX ? (
          <Strong>{t('Inbox')}</Strong>
        ) : (
          t('Inbox')
        )}
      </MenuItem>
      <MenuItem
        data={{ cy: 'status-menu-item-prescreen' }}
        key="prescreen"
        onClick={() => {
          if (currentBucket !== BUCKETS.PRESCREEN) {
            changeStatus(BUCKETS.PRESCREEN);
          }
        }}
      >
        {currentBucket === BUCKETS.PRESCREEN ? (
          <Strong>{t('Prescreen')}</Strong>
        ) : (
          t('Prescreen')
        )}
      </MenuItem>
      <MenuItem
        data={{ cy: 'status-menu-item-shortlist' }}
        key="shortlist"
        onClick={() => {
          if (currentBucket !== BUCKETS.SHORTLIST) {
            changeStatus(BUCKETS.SHORTLIST);
          }
        }}
      >
        {currentBucket === BUCKETS.SHORTLIST ? (
          <Strong>{t('Shortlist')}</Strong>
        ) : (
          t('Shortlist')
        )}
      </MenuItem>
      <MenuItem
        data={{ cy: 'status-menu-item-interview' }}
        key="interview"
        onClick={() => {
          if (currentBucket !== BUCKETS.INTERVIEW) {
            changeStatus(BUCKETS.INTERVIEW);
          }
        }}
      >
        {currentBucket === BUCKETS.INTERVIEW ? (
          <Strong>{t('Interview')}</Strong>
        ) : (
          t('Interview')
        )}
      </MenuItem>
      <MenuItem
        data={{ cy: 'status-menu-item-offer' }}
        key="offer"
        onClick={() => {
          if (currentBucket !== BUCKETS.OFFER) {
            changeStatus(BUCKETS.OFFER);
          }
        }}
      >
        {currentBucket === BUCKETS.OFFER ? (
          <Strong>{t('Offer')}</Strong>
        ) : (
          t('Offer')
        )}
      </MenuItem>
      <MenuItem
        data={{ cy: 'status-menu-item-accept' }}
        key="offer"
        onClick={() => {
          if (currentBucket !== BUCKETS.ACCEPT) {
            changeStatus(BUCKETS.ACCEPT);
          }
        }}
      >
        {currentBucket === BUCKETS.ACCEPT ? (
          <Strong>{t('Accept')}</Strong>
        ) : (
          t('Accept')
        )}
      </MenuItem>
      <MenuItem
        data={{ cy: 'status-menu-item-not_suitable' }}
        key="offer"
        onClick={() => {
          if (currentBucket !== BUCKETS.NOT_SUITABLE) {
            changeStatus(BUCKETS.NOT_SUITABLE);
          }
        }}
      >
        {currentBucket === BUCKETS.NOT_SUITABLE ? (
          <Strong>{t('Not Suitable')}</Strong>
        ) : (
          t('Not Suitable')
        )}
      </MenuItem>
    </>
  );

  return (
    <MenuRenderer
      data={{ cy: 'status-menu' }}
      offsetSpace="xxsmall"
      width="small"
      placement={
        responsiveValue({ mobile: 'top', tablet: 'bottom' }) ?? 'bottom'
      }
      align={responsiveValue({ mobile: 'left', tablet: 'right' }) ?? 'right'}
      trigger={(triggerProps) =>
        currentBucket === BUCKETS.INBOX ||
        (!isMobile && currentBucket === BUCKETS.INTERVIEW) ? (
          <TooltipRenderer
            id="move-status-menu"
            tooltip={<Text size="small">{t('Move to')}</Text>}
          >
            {({ triggerProps: tooltipTriggerProps }) => (
              <Box
                {...tooltipTriggerProps}
                borderRadius="standard"
                className="focusRing"
              >
                <Button
                  {...triggerProps}
                  size="small"
                  tone="neutral"
                  variant="solid"
                  aria-label={menuLabel}
                  tabIndex={-1}
                >
                  <IconArrow direction="right" />
                </Button>
              </Box>
            )}
          </TooltipRenderer>
        ) : (
          <Button
            {...triggerProps}
            size="small"
            tone="neutral"
            variant="solid"
            aria-label={menuLabel}
            icon={<IconArrow direction="right" />}
            iconPosition="trailing"
          >
            {t('Move to')}
          </Button>
        )
      }
    >
      {menuItems}
    </MenuRenderer>
  );
};
